import React, { forwardRef, useRef } from "react";
import { cn } from "../lib/utils";
import { AnimatedBeam } from "./ui/AnimatedBeam";
import {
  IconDiscount,
  IconHeadset,
  IconRecycle,
  IconSettings,
  IconShieldCheck,
  IconChartPie,
  IconBrain,
  IconBox,
} from "@tabler/icons-react";

const Circle = forwardRef<
  HTMLDivElement,
  { className?: string; children?: React.ReactNode; icon: React.ReactNode }
>(({ className, children, icon }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "z-10 flex w-[fit-content] h-auto items-center justify-center rounded-full border-2 border-border bg-white px-5 py-3 shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)]",
        className
      )}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="hidden md:inline">{children}</span>
      </div>
    </div>
  );
});

Circle.displayName = "Circle";

export function AnimatedBeamDemo({ className }: { className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const div1Ref = useRef<HTMLDivElement>(null);
  const div2Ref = useRef<HTMLDivElement>(null);
  const div3Ref = useRef<HTMLDivElement>(null);
  const div4Ref = useRef<HTMLDivElement>(null);
  const div5Ref = useRef<HTMLDivElement>(null);
  const div6Ref = useRef<HTMLDivElement>(null);
  const div7Ref = useRef<HTMLDivElement>(null);
  const div8Ref = useRef<HTMLDivElement>(null);

  return (
    <div
      className={cn(
        "relative flex flex-col items-center min-h-[500px] w-full p-10",
        className
      )}
      ref={containerRef}
    >
      {/* Heading and Subheading */}
      <div className="text-4xl text-[#16423C] md:text-5xl pb-4 font-bold text-center tracking-tight">
        Packaging optimization
      </div>
      <div className="text-xl text-[#16423C] font-medium mb-12 text-center max-w-5xl pb-8 lg:pb-16 tracking-tight">
        Maximize your packaging development potential through the unison of
        predictive insights and optimization techniques, crafting a streamlined
        experimental approach that is both meticulously structured and timely.
      </div>

      {/* Main Content */}
      <div className="flex w-full max-w-4xl flex-row items-stretch justify-between gap-10 text-[#16423C] font-semibold">
        <div className="flex flex-col justify-center gap-4">
          <Circle
            className="bg-[#E9EFEC]"
            ref={div1Ref}
            icon={<IconChartPie />}
          >
            Life cycle assessment
          </Circle>
          <Circle
            className="bg-[#E9EFEC]"
            ref={div2Ref}
            icon={<IconShieldCheck />}
          >
            Protective functions
          </Circle>
          <Circle
            className="bg-[#E9EFEC]"
            ref={div3Ref}
            icon={<IconSettings />}
          >
            Mechanical properties
          </Circle>
          <Circle className="bg-[#E9EFEC]" ref={div4Ref} icon={<IconRecycle />}>
            Recyclability
          </Circle>
          <Circle className="bg-[#E9EFEC]" ref={div5Ref} icon={<IconHeadset />}>
            Customer response
          </Circle>
          <Circle
            className="bg-[#E9EFEC]"
            ref={div8Ref}
            icon={<IconDiscount />}
          >
            Costs and resources
          </Circle>
        </div>
        <div className="flex flex-col justify-center text-[#16423C] font-semibold">
          <Circle
            ref={div6Ref}
            className="max-w-[250px] text-center bg-[#E9EFEC]"
            icon={<IconBrain size={40} />}
          >
            Objective function and AI-assisted optimization
          </Circle>
        </div>
        <div className="flex flex-col justify-center text-[#16423C] font-semibold">
          <Circle className="bg-[#E9EFEC]" ref={div7Ref} icon={<IconBox />}>
            Product & Packaging design
          </Circle>
        </div>
      </div>

      {/* Animated Beams */}
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div1Ref}
        toRef={div6Ref}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div8Ref}
        toRef={div6Ref}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div2Ref}
        toRef={div6Ref}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div3Ref}
        toRef={div6Ref}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div4Ref}
        toRef={div6Ref}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div5Ref}
        toRef={div6Ref}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div6Ref}
        toRef={div7Ref}
      />
    </div>
  );
}
